/**
 * Non-blocking "Version X available — View notes · Update now" banner for
 * admins on a LOCAL install (OFFLINE-DISTRIBUTION-PLAN.md §3.3). Turns red
 * with the fixed lock text when the install sits below a required release's
 * minimum version. Ported from ../lims's `app/UpdateBanner.tsx` onto the same
 * full-width banner strip AppLayout's StatusBanner uses.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { compareVersions, LOCK_MESSAGES, PERMISSIONS } from '@pospak/shared';
import { ABOUT_SETTINGS_PATH, useUpdateStatusQuery } from '../lib/updates-api';
import { useAuth } from '../stores/auth';
import { ReleaseNotesDialog, UpdateApplyDialog } from './update-flow';

export function UpdateBanner() {
  const hasPermission = useAuth((s) => s.hasPermission);
  const isAdmin = hasPermission(PERMISSIONS.SETTINGS_MANAGE);
  const statusQuery = useUpdateStatusQuery({ enabled: isAdmin });
  const [notesOpen, setNotesOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);

  const status = statusQuery.data;
  if (!isAdmin || !status || status.deploymentMode !== 'LOCAL') return null;

  const pending = status.pendingUpdate;
  if (!pending) {
    if (!status.lastUpdateFailed) return null;
    return (
      <div role="status" className="flex items-center justify-center gap-2 bg-warn px-4 py-2 text-center text-sm text-white">
        <AlertTriangle size={16} className="flex-none" aria-hidden />
        The last update did not complete and was rolled back.
        <Link to={ABOUT_SETTINGS_PATH} className="underline underline-offset-2">
          See details
        </Link>
      </div>
    );
  }

  const blocked = pending.isRequired && pending.minimumAllowedVersion !== null && compareVersions(status.runningVersion, pending.minimumAllowedVersion) < 0;

  return (
    <>
      <div
        role={blocked ? 'alert' : 'status'}
        className={`flex flex-wrap items-center justify-center gap-3 px-4 py-2 text-center text-sm text-white ${blocked ? 'bg-danger' : 'bg-info'}`}
      >
        <ArrowUpCircle size={16} className="flex-none" aria-hidden />
        <span>{blocked ? LOCK_MESSAGES.VERSION : `Version ${pending.version} available`}</span>
        <span className="opacity-60">·</span>
        <button type="button" className="underline underline-offset-2" onClick={() => setNotesOpen(true)}>
          View notes
        </button>
        <span className="opacity-60">·</span>
        <button type="button" className="font-semibold underline underline-offset-2" onClick={() => setApplyOpen(true)}>
          Update now
        </button>
      </div>
      <ReleaseNotesDialog version={pending.version} notes={pending.notes} open={notesOpen} onOpenChange={setNotesOpen} />
      <UpdateApplyDialog status={status} open={applyOpen} onOpenChange={setApplyOpen} />
    </>
  );
}
